import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useMemo, useState } from "react";

import { UsePaginationFetchMoreProps } from "components/Table/Pagination/types";
import usePaginationCursorReset from "components/Table/Pagination/usePaginationCursorReset";
import useSearchStackTasks from "shared/Stack/useSearchTasks";
import useSearchStackTasksSuggestions from "shared/Stack/useSearchTasksSuggestions";
import { SearchQueryRequestedPage } from "types/generated";

import { STACK_TASKS_LIMIT, STACK_TASKS_LIMIT_STORAGE_KEY } from "./constants";

const useStackTasksList = (stackId: string) => {
  const [storedRowsPerPage] = useLocalStorage<number>(STACK_TASKS_LIMIT_STORAGE_KEY);
  const [storedCursor, setStoredCursor] = useState<string | null>(null);
  const [storedRequestedPage, setStoredRequestedPage] = useState(SearchQueryRequestedPage.First);

  const onFetchItems = useCallback(({ cursor, requestedPage }: UsePaginationFetchMoreProps) => {
    setStoredCursor(cursor);
    setStoredRequestedPage(requestedPage);
  }, []);

  const resetPagination = useCallback(() => {
    setStoredCursor(null);
    setStoredRequestedPage(SearchQueryRequestedPage.First);
  }, []);
  const paginationResetKey = useMemo(() => ({ stackId }), [stackId]);

  const { cursor, requestedPage } = usePaginationCursorReset({
    resetKey: paginationResetKey,
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  });

  const { tasks, endCursor, startCursor, error, initialLoading, loadingContent } =
    useSearchStackTasks(stackId, {
      first: storedRowsPerPage || STACK_TASKS_LIMIT,
      after: cursor,
      fullTextSearch: null,
      orderBy: null,
      requestedPage,
    });

  const { totalCount } = useSearchStackTasksSuggestions(stackId, {
    fields: [],
    fullTextSearch: null,
  });

  return {
    tasks,
    error,
    isPageLoading: initialLoading,
    loadingContent,
    onFetchItems,
    endCursor,
    startCursor,
    totalCount,
    paginationResetKey,
  };
};

export default useStackTasksList;
